import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import apiEndPoints from '../../apiconstants';
import { api, handleErrorResponse, responseArgs } from '../api';
import type { AIWorker } from '../aides/aiworkerTypes';

/**
 * Configurable Insight agents (cty-ai-data-access poc/insight-agent DESIGN.md §2).
 *
 * An agent is an `analyst` aiworker with sub_type `insight_agent`; its configuration and each
 * tenant guidance item are tenant-scope rows in configuration_management. Saving is PATCH, which
 * writes a new version and activates it. A row is POSTed exactly once: a second POST leaves two
 * active rows.
 */
export const INSIGHT_AGENT_CATEGORY = 'analyst';
export const INSIGHT_AGENT_SUB_TYPE = 'insight_agent';
export const INSIGHT_AGENT_CONFIG_NAME = 'insight_agent';
/**
 * DOC's aiworker PATCH refuses any analyst without an analyst_type attr ("analyst_type not found"),
 * so agents carry one; only "descriptive" has further requirements there.
 */
export const INSIGHT_AGENT_ANALYST_TYPE = 'insight_agent';
export const INSIGHT_GUIDANCE_CONFIG_NAME = 'insight_guidance';
/** A saved AI Insight dashboard: widget definitions only, never rows. */
export const INSIGHT_DASHBOARD_CONFIG_NAME = 'insight_dashboard';
/**
 * A dashboard shared with the tenant is also listed in All Analysts, as an analyst aiworker with this
 * sub_type and analyst_type whose `dashboard_id` attribute names the dashboard's config_id. The
 * dashboard itself stays in configuration_management; the aiworker only lists it.
 */
export const INSIGHT_DASHBOARD_SUB_TYPE = 'insight_dashboard';
export const INSIGHT_DASHBOARD_ANALYST_TYPE = 'insight_dashboard';
export const INSIGHT_DASHBOARD_ID_ATTR = 'dashboard_id';

export type InsightConfigName =
  | typeof INSIGHT_AGENT_CONFIG_NAME
  | typeof INSIGHT_GUIDANCE_CONFIG_NAME
  | typeof INSIGHT_DASHBOARD_CONFIG_NAME;

export interface InsightConfigRow<TConfig = Record<string, unknown>> {
  configuration_id: string;
  config_id: string;
  aiworker_id?: string | null;
  name?: string;
  config_name?: string;
  version?: number;
  version_id?: string;
  status?: string;
  is_active?: boolean;
  config: TConfig;
  updated_at?: string;
  updated_by?: string;
}

/** A row of POST /configuration_management/versions (no config payload). A type, so it fits VersionCompareModal. */
export type InsightConfigVersion = {
  configuration_id: string;
  config_id: string;
  version?: number;
  version_id?: string;
  status?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  updated_by?: string;
  updated_by_email?: string | null;
};

export interface InsightConfigFilters {
  config_name: InsightConfigName;
  config_id?: string;
  version_id?: string;
  is_active?: boolean;
}

/** Errors keep the HTTP status: a 409 on save means the row is no longer the active version. */
export interface InsightApiError {
  status?: number | string;
  message: string;
}

function insightError(response: FetchBaseQueryError): InsightApiError {
  const handled = handleErrorResponse(response) as { message?: string; status?: number } | undefined;
  return {
    status: typeof response?.status === 'number' ? response.status : handled?.status,
    message: handled?.message ?? 'An unexpected error occurred',
  };
}

const configuration = (response: responseArgs) => response?.data?.configuration as InsightConfigRow;

export const insightAgentApiSlice = api.injectEndpoints({
  endpoints: (builder) => ({
    getInsightAgents: builder.query<AIWorker[], void>({
      query: () => `${apiEndPoints.getAiWorkersByCategory}?category=${INSIGHT_AGENT_CATEGORY}&sub_type=${INSIGHT_AGENT_SUB_TYPE}`,
      transformResponse: (response: responseArgs) => (Array.isArray(response?.data) ? response.data : []),
      transformErrorResponse: insightError,
    }),
    queryInsightConfigs: builder.query<InsightConfigRow[], InsightConfigFilters>({
      query: (filters) => ({
        url: apiEndPoints.configurationManagementQuery,
        method: 'POST',
        body: { scope: 'tenant', filters, pagination: { page: 1, per_page: 100 } },
      }),
      // Newest version first; superseded versions are 'inactive', deleted rows are never returned.
      transformResponse: (response: responseArgs) => response?.data?.configurations ?? [],
      transformErrorResponse: insightError,
    }),
    getInsightConfigVersions: builder.query<InsightConfigVersion[], { config_id: string; config_name: InsightConfigName }>({
      query: ({ config_id, config_name }) => ({
        url: `${apiEndPoints.configurationManagement}/versions`,
        method: 'POST',
        body: { scope: 'tenant', config_id, config_name, pagination: { page: 1, per_page: 100 } },
      }),
      transformResponse: (response: responseArgs) => response?.data?.versions ?? [],
      transformErrorResponse: insightError,
    }),
    /** `code` names a well-known agent (the tenant's default Insight Agent); DOC refuses a code in use. */
    createInsightAgentWorker: builder.mutation<AIWorker, { name: string; description: string; code?: string }>({
      query: ({ name, description, code }) => ({
        url: apiEndPoints.createAiWorkers,
        method: 'POST',
        body: {
          name,
          description,
          category: INSIGHT_AGENT_CATEGORY,
          type: 'service',
          sub_type: INSIGHT_AGENT_SUB_TYPE,
          ...(code ? { code } : {}),
          aiworkers_attrs: [{ attr_name: 'analyst_type', attr_value: INSIGHT_AGENT_ANALYST_TYPE }],
        },
      }),
      transformResponse: (response: responseArgs) => response?.data as AIWorker,
      transformErrorResponse: insightError,
    }),
    /** The analysts that list dashboards in All Analysts (the ones this user may read). */
    getInsightDashboardAnalysts: builder.query<AIWorker[], void>({
      query: () => `${apiEndPoints.getAiWorkersByCategory}?category=${INSIGHT_AGENT_CATEGORY}&sub_type=${INSIGHT_DASHBOARD_SUB_TYPE}`,
      transformResponse: (response: responseArgs) => (Array.isArray(response?.data) ? response.data : []),
      transformErrorResponse: insightError,
    }),
    /** Lists a dashboard in All Analysts. DOC refuses a name already used by an aiworker or advisor. */
    createInsightDashboardAnalyst: builder.mutation<AIWorker, { name: string; description: string; dashboard_id: string }>({
      query: ({ name, description, dashboard_id }) => ({
        url: apiEndPoints.createAiWorkers,
        method: 'POST',
        body: {
          name,
          description,
          category: INSIGHT_AGENT_CATEGORY,
          type: 'service',
          sub_type: INSIGHT_DASHBOARD_SUB_TYPE,
          aiworkers_attrs: [
            { attr_name: 'analyst_type', attr_value: INSIGHT_DASHBOARD_ANALYST_TYPE },
            { attr_name: INSIGHT_DASHBOARD_ID_ATTR, attr_value: dashboard_id },
          ],
        },
      }),
      transformResponse: (response: responseArgs) => response?.data as AIWorker,
      transformErrorResponse: insightError,
    }),
    /** Name and description live on the aiworker, not in the configuration (Insight agents and dashboard analysts). */
    updateInsightAgentWorker: builder.mutation<AIWorker, { aiworker_id: string; name: string; description: string }>({
      query: ({ aiworker_id, name, description }) => ({
        url: `${apiEndPoints.updateAiWorkers}/${aiworker_id}`,
        method: 'PATCH',
        body: { name, description },
      }),
      transformResponse: (response: responseArgs) => response?.data as AIWorker,
      transformErrorResponse: insightError,
    }),
    /** POST once per config_id. Every later save is updateInsightConfig. */
    createInsightConfig: builder.mutation<
      InsightConfigRow,
      { config_id: string; aiworker_id?: string; name: string; config_name: InsightConfigName; config: object }
    >({
      query: ({ config_id, aiworker_id, name, config_name, config }) => ({
        url: apiEndPoints.configurationManagement,
        method: 'POST',
        body: {
          scope: 'tenant',
          name,
          config_id,
          ...(aiworker_id ? { aiworker_id } : {}),
          category: 'aiworker',
          sub_category: config_name,
          config_name,
          status: 'active',
          is_active: true,
          config,
        },
      }),
      transformResponse: configuration,
      transformErrorResponse: insightError,
    }),
    /** Copy-on-write: DOC writes a new version and activates it. configuration_id must be the active row's (else 409). */
    updateInsightConfig: builder.mutation<InsightConfigRow, { configuration_id: string; name?: string; config: object }>({
      query: ({ configuration_id, name, config }) => ({
        url: `${apiEndPoints.configurationManagement}/${configuration_id}`,
        method: 'PATCH',
        body: { scope: 'tenant', ...(name ? { name } : {}), config },
      }),
      transformResponse: configuration,
      transformErrorResponse: insightError,
    }),
    /**
     * Soft delete of ONE row (DOC sets status 'deleted' on that configuration_id only). To delete a
     * guidance item, delete every version: an inactive one could otherwise be rolled back to.
     */
    deleteInsightConfig: builder.mutation<void, { configuration_id: string }>({
      query: ({ configuration_id }) => ({
        url: `${apiEndPoints.configurationManagement}/tenant/${configuration_id}`,
        method: 'DELETE',
      }),
      transformErrorResponse: insightError,
    }),
    rollbackInsightConfig: builder.mutation<InsightConfigRow, { config_id: string; version_id: string; config_name: InsightConfigName }>({
      query: ({ config_id, version_id, config_name }) => ({
        url: `${apiEndPoints.configurationManagement}/rollback`,
        method: 'PATCH',
        body: { scope: 'tenant', config_id, version_id, config_name },
      }),
      transformResponse: configuration,
      transformErrorResponse: insightError,
    }),
  }),
});

export const {
  useGetInsightAgentsQuery,
  useLazyGetInsightAgentsQuery,
  useQueryInsightConfigsQuery,
  useLazyQueryInsightConfigsQuery,
  useGetInsightConfigVersionsQuery,
  useLazyGetInsightConfigVersionsQuery,
  useDeleteInsightConfigMutation,
  useCreateInsightAgentWorkerMutation,
  useUpdateInsightAgentWorkerMutation,
  useLazyGetInsightDashboardAnalystsQuery,
  useCreateInsightDashboardAnalystMutation,
  useCreateInsightConfigMutation,
  useUpdateInsightConfigMutation,
  useRollbackInsightConfigMutation,
} = insightAgentApiSlice;
